// ui/useTrickAnimation.js: rAF time accumulator hook (M2.c-4).
//
// Why rAF instead of setTimeout(600)? When a tab is backgrounded the
// browser throttles timers; cards could sit mid-sweep for minutes. rAF
// pauses naturally when the tab is hidden and resumes on visibility,
// the elapsed_ms accumulator stays accurate to real frame time, so a
// resume picks up smoothly (eng EM2-6).
//
// API:
//   useTrickAnimation({ active, durationMs, onComplete })
//     → elapsedMs  (number, 0 while inactive, monotonically increasing
//                   while active up to durationMs, then frozen at
//                   durationMs and onComplete fires once)
//
// Caller flips `active` to true to start; the hook resets the
// accumulator when active transitions false → true.

import { useEffect, useRef, useState } from 'preact/hooks';

/**
 * @param {{
 *   active: boolean,
 *   durationMs: number,
 *   onComplete?: () => void,
 * }} opts
 */
export function useTrickAnimation({ active, durationMs, onComplete }) {
  const [elapsedMs, setElapsedMs] = useState(0);
  const rafRef = useRef(/** @type {number|null} */ (null));
  const startRef = useRef(/** @type {number|null} */ (null));
  const completedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!active) {
      // Reset on inactive, next activation starts from 0.
      startRef.current = null;
      completedRef.current = false;
      setElapsedMs(0);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    const tick = (now) => {
      if (startRef.current === null) startRef.current = now;
      const start = /** @type {number} */ (startRef.current);
      const elapsed = Math.min(durationMs, now - start);
      setElapsedMs(elapsed);
      if (elapsed >= durationMs) {
        if (!completedRef.current) {
          completedRef.current = true;
          onCompleteRef.current?.();
        }
        rafRef.current = null;
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [active, durationMs]);

  return elapsedMs;
}

/**
 * Hook reading prefers-reduced-motion at mount. Updates if the user
 * toggles the OS setting mid-session.
 */
export function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (e) => setReduced(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
  return reduced;
}
